// The Price map's Mismatches (AdminPrices.dc.html): a payment that opened nothing (paid_no_access,
// raised the moment Stripe's event lands), access with no payment and no reason (access_no_payment,
// the morning check), and Stripe prices that aren't on the map at all (read from Stripe when the
// page opens). Each card's words follow its cause (S05), and its 2 buttons follow the cause too
// (the critic's wrong_or_contradictory 9).
//
// Writes: resolve-payment and revoke-access (Edge Functions, owner and admin), a reason on the access
// row and a job ticked off (direct writes under row level security, owner and admin), and for an
// owner, mapping a price (forms.jsx). Paid, but no access and Access, but no payment are Today's jobs
// too: their words, buttons and what each does are src/admin/mismatches.js's, so the 2 screens agree.
import { useState } from 'react'
import { Button, ConfirmPanel, Field, TextInput, SearchBox, useAdmin } from '../ui/index.js'
import { MISMATCH, unmappedText, doneText, labelFromStripeName } from './model.js'
import {
  MISMATCH_WORDS as MW, RESOLUTION, paidText, paidActions, unmappedPricesOf, createNeedsEmail, emailOk, noPaymentText, reasonDone,
  reasonLabel, reasonHint, revokeAsk, revokeDone,
} from '../mismatches.js'

function Card({ kind, text, done, children }) {
  return (
    <div className={`pm-issue${done ? ' pm-issue--done' : ''}`}>
      <span className="pm-issue-kind">{kind}</span>
      <span className="pm-issue-text">{text}</span>
      {done ? <span className="pm-issue-done" role="status">{done}</span> : children}
    </div>
  )
}

function Actions({ children }) {
  return <span className="pm-issue-actions">{children}</span>
}

// Find someone already in the hub to apply a payment to (owner, admin)
function MatchPicker({ onPick, onCancel, busy }) {
  const { api } = useAdmin()
  const [q, setQ] = useState('')
  const [found, setFound] = useState(null)
  const [picked, setPicked] = useState(null)
  const [error, setError] = useState(null)

  async function search(text) {
    setQ(text)
    setPicked(null)
    if (text.trim().length < 2) { setFound(null); return }
    try {
      const rows = await api.staff_people({ search: text.trim(), limit: 6 })
      setFound(rows)
      setError(null)
    } catch (e) {
      setError(api.words(e))
    }
  }

  if (picked) {
    return (
      <ConfirmPanel text={`Apply this payment to ${picked.full_name} (${picked.email})? What it paid for opens for ${picked.full_name.split(' ')[0] || picked.full_name}, and the access email goes to that address.`}
        confirmLabel="Apply the payment" cancelLabel="Pick someone else" busy={busy}
        onConfirm={() => onPick(picked)} onCancel={() => setPicked(null)} />
    )
  }
  return (
    <div className="pm-match">
      <SearchBox label={MW.search} value={q} onChange={search} />
      {error && <p className="ad-error" role="alert">{error}</p>}
      {found && found.length === 0 && <p className="pm-match-none">{MW.nobody}</p>}
      {found && found.length > 0 && (
        <ul className="pm-match-list" aria-label="People who match">
          {found.map(p => (
            <li key={p.person_id}>
              <span className="pm-match-who"><span className="pm-match-name">{p.full_name}</span><span className="pm-match-email">{p.email}</span></span>
              <Button size="xs" onClick={() => setPicked({ id: p.person_id, full_name: p.full_name, email: p.email })}>Pick</Button>
            </li>
          ))}
        </ul>
      )}
      <Actions><Button size="sm" kind="quiet" onClick={onCancel}>{MW.cancel}</Button></Actions>
    </div>
  )
}

// A payment that opened nothing
export function PaidCard({ job, amount, role, mappedIds, onMap, onChanged, doneLocal, setDone }) {
  const { api } = useAdmin()
  const [open, setOpen] = useState(null) // 'match' | 'email' | 'handled'
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const missing = unmappedPricesOf(job)
  // the price has been mapped since: the held payment can be applied now
  const nowMapped = missing.length > 0 && missing.every(id => mappedIds.has(id))
  const actions = paidActions(job, role, { mapped: nowMapped })
  const done = doneLocal || (job.status === 'done' ? doneText(job) : null)

  async function resolve(body, okLine) {
    setBusy(true)
    setError(null)
    try {
      const answer = await api.resolve_payment({ flag_id: job.flag_ids[0], ...body })
      setDone(okLine ? okLine(answer) : answer.message)
      onChanged()
    } catch (e) {
      setError(api.words(e))
    } finally {
      setBusy(false)
    }
  }

  async function handled() {
    setBusy(true)
    setError(null)
    try {
      await api.tick_job(job.flag_ids, { resolution: RESOLUTION.handled })
      setDone(MW.handledDone)
      onChanged()
    } catch (e) {
      setError(api.words(e))
    } finally {
      setBusy(false)
    }
  }

  function createWithEmail() {
    if (!emailOk(email)) { setError(MW.emailWrong); return }
    resolve({ email: email.trim() })
  }

  return (
    <Card kind={MW.kindPaid} text={paidText(job, amount)} done={done}>
      {!open && (
        <Actions>
          {actions.includes('map') && <Button kind="ink" size="sm" onClick={() => onMap(job)}>{MW.map}</Button>}
          {actions.includes('apply') && <Button kind="ink" size="sm" busy={busy} onClick={() => resolve({})}>{MW.apply}</Button>}
          {actions.includes('create') && (
            <Button kind="ink" size="sm" busy={busy} busyLabel={MW.creating}
              onClick={() => (createNeedsEmail(job) ? setOpen('email') : resolve({}))}>{MW.create}</Button>
          )}
          {actions.includes('match') && <Button size="sm" onClick={() => setOpen('match')} disabled={busy}>{MW.match}</Button>}
          {actions.includes('handled') && <Button size="sm" onClick={() => setOpen('handled')} disabled={busy}>{MW.handled}</Button>}
        </Actions>
      )}
      {!open && job.detail?.reason === 'unmapped_price' && role !== 'owner' && !nowMapped && <span className="pm-issue-note">{MW.ownerMaps}</span>}
      {open === 'email' && (
        <div className="pm-inline">
          <Field label={MW.email} hint={MW.emailHint}>
            {props => <TextInput {...props} type="email" value={email} onChange={setEmail} autoComplete="off" />}
          </Field>
          <Actions>
            <Button kind="ink" size="sm" busy={busy} busyLabel={MW.creating} onClick={createWithEmail}>{MW.create}</Button>
            <Button size="sm" kind="quiet" onClick={() => { setOpen(null); setError(null) }} disabled={busy}>{MW.cancel}</Button>
          </Actions>
        </div>
      )}
      {open === 'match' && <MatchPicker busy={busy} onCancel={() => setOpen(null)} onPick={p => resolve({ person_id: p.id })} />}
      {open === 'handled' && (
        <ConfirmPanel text={MW.handledAsk}
          confirmLabel={MW.handled} cancelLabel={MW.keepIt} busy={busy} error={error} onConfirm={handled} onCancel={() => { setOpen(null); setError(null) }} />
      )}
      {error && open !== 'handled' && <p className="ad-error" role="alert">{error}</p>}
    </Card>
  )
}

// Access with no payment behind it and no reason logged
export function NoPaymentCard({ job, onChanged, doneLocal, setDone }) {
  const { api } = useAdmin()
  const [open, setOpen] = useState(null) // 'reason' | 'revoke'
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const done = doneLocal || (job.status === 'done' ? doneText(job) : null)

  async function saveReason() {
    const text = reason.trim()
    if (!text) { setError(MW.reasonNeeded); return }
    setBusy(true)
    setError(null)
    try {
      await api.update_access(job.access_id, { reason: text })
      // the morning check would close it tomorrow; it's closed now, so Today agrees (the reason stands
      // if the tick doesn't go, as on Today, and the morning check closes it)
      try { await api.tick_job(job.flag_ids, { resolution: RESOLUTION.reason }) } catch { /* see above */ }
      setDone(reasonDone(text))
      onChanged()
    } catch (e) {
      setError(api.words(e))
    } finally {
      setBusy(false)
    }
  }

  async function revoke() {
    setBusy(true)
    setError(null)
    try {
      await api.revoke_access({ access_id: job.access_id, end_reason: 'revoked_by_staff' })
      try { await api.tick_job(job.flag_ids, { resolution: RESOLUTION.revoked }) } catch { /* the revoke stands; the morning check closes it */ }
      setDone(revokeDone(job))
      onChanged()
    } catch (e) {
      setError(api.words(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card kind={MW.kindNoPayment} text={noPaymentText(job)} done={done}>
      {!open && (
        <Actions>
          <Button kind="ink" size="sm" onClick={() => setOpen('reason')}>{MW.addReason}</Button>
          <Button size="sm" onClick={() => setOpen('revoke')}>{MW.revoke}</Button>
        </Actions>
      )}
      {open === 'reason' && (
        <div className="pm-inline">
          <Field label={reasonLabel(job)} hint={reasonHint(job)}>
            {props => <TextInput {...props} value={reason} onChange={setReason} maxLength={1000} />}
          </Field>
          <Actions>
            <Button kind="ink" size="sm" busy={busy} busyLabel={MW.saving} onClick={saveReason}>{MW.saveReason}</Button>
            <Button size="sm" kind="quiet" onClick={() => { setOpen(null); setError(null) }} disabled={busy}>{MW.cancel}</Button>
          </Actions>
        </div>
      )}
      {open === 'revoke' && (
        <ConfirmPanel text={revokeAsk(job)}
          confirmLabel={MW.revokeAccess} cancelLabel={MW.keepIt} busy={busy} error={error} onConfirm={revoke} onCancel={() => { setOpen(null); setError(null) }} />
      )}
      {error && open !== 'revoke' && <p className="ad-error" role="alert">{error}</p>}
    </Card>
  )
}

// A Stripe price that isn't on the map at all
export function UnmappedCard({ price, role, onMap, onChanged, doneLocal, setDone }) {
  const { api, personId } = useAdmin()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  async function leaveOut() {
    if (!price.product_id) { setError("Stripe didn't say which product this price belongs to, so it can't be marked here."); return }
    setBusy(true)
    setError(null)
    try {
      await api.run(api.from('price_map').insert({
        stripe_price_id: price.id, stripe_product_id: price.product_id,
        label: labelFromStripeName(price.product_name) || 'A Stripe price',
        status: 'not_in_hub', updated_by: personId,
      }).select('stripe_price_id, status'))
      setDone('Marked as not in the hub. A payment on it is logged and opens nothing.')
      onChanged()
    } catch (e) {
      setError(api.words(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card kind={MISMATCH.kindUnmapped} text={unmappedText(price)} done={doneLocal}>
      {role === 'owner' && !open && (
        <Actions>
          <Button kind="ink" size="sm" onClick={() => onMap(price)}>{MISMATCH.mapIt}</Button>
          <Button size="sm" onClick={() => setOpen(true)}>{MISMATCH.leaveOut}</Button>
        </Actions>
      )}
      {role === 'owner' && open && (
        <ConfirmPanel text="Mark it as not in the hub? A payment on it is then logged and opens nothing, and doesn't show here."
          confirmLabel={MISMATCH.leaveOut} cancelLabel="Keep it" busy={busy} error={error} onConfirm={leaveOut} onCancel={() => { setOpen(false); setError(null) }} />
      )}
      {role !== 'owner' && <span className="pm-issue-note">{MISMATCH.ownerMaps}</span>}
      {error && !open && <p className="ad-error" role="alert">{error}</p>}
    </Card>
  )
}
